import React, { useCallback } from "react";
import * as XLSX from "xlsx";

import MainPageHOC from "../../layout/hoc/MainPageHOC";
import MainPageTableHeader from "../../layout/body/MainPageTableHeader";
import StudentsPageFilters from "../../components/studentPage/StudentsPageFilters";

import PaginationComponent from "../../components/pagination/PaginationComponent";
import FetcherComponent from "../../components/shared/FetcherComponent";
import MainPageHeader from "../../components/shared/MainPageHeader";

import { studentService } from "../../services/studentService";
import { getAcademicYearDateRange } from "../../utilities/dateTimeUtils";
import {
  ACADEMIC_YEAR_OPTIONS,
  FACULTIES_ENUM_READABLE,
  FILTER_PARAM_KEYS,
  HEAR_ABOUT_US_READABLE,
} from "../../utilities/constants";

import { useStudentPageContext } from "../../hooks/useStudentPageContext";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import StudentsPageTableBody from "../../components/studentPage/StudentsPageTableBody";
import { formatDate } from "../../utilities/dateTimeUtils";

const readFiltersFromUrl = () => {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const ay = p.get(FILTER_PARAM_KEYS.ACADEMIC_YEAR);
  const ft = p.get(FILTER_PARAM_KEYS.FACULTY_TYPE);
  const df = p.get(FILTER_PARAM_KEYS.DATE_FROM) || "";
  const dt = p.get(FILTER_PARAM_KEYS.DATE_TO) || "";
  const st = p.get(FILTER_PARAM_KEYS.STATUS);
  const search = p.get(FILTER_PARAM_KEYS.SEARCH) || "";
  return {
    academicYear: ay
      ? ACADEMIC_YEAR_OPTIONS.find((o) => o.value === ay) || {
          value: ay,
          label: ay,
        }
      : null,
    facultyType: ft
      ? { value: ft, label: FACULTIES_ENUM_READABLE[ft] || ft }
      : null,
    createdDateFrom: df,
    createdDateTo: dt,
    activeStatusTab: st || null,
    search: search || "",
  };
};

const StudentsPage = () => {
  const {
    students,
    setStudents,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalRecords,
    setTotalRecords,
    setPageSize,
    pageSize,
  } = useStudentPageContext();
  const [onFetch] = useBeManualFetcher();

  const [academicYear, setAcademicYear] = React.useState(
    () => readFiltersFromUrl().academicYear
  );
  const [facultyType, setFacultyType] = React.useState(
    () => readFiltersFromUrl().facultyType
  );
  const [createdDateFrom, setCreatedDateFrom] = React.useState(
    () => readFiltersFromUrl().createdDateFrom
  );
  const [createdDateTo, setCreatedDateTo] = React.useState(
    () => readFiltersFromUrl().createdDateTo
  );
  const [activeStatusTab, setActiveStatusTab] = React.useState(
    () => readFiltersFromUrl().activeStatusTab
  );

  const {
    search,
    setSearch,
    searchQuery,
    updateFilterParams,
    clearFilterParams,
    searchParams,
  } = useUrlFilters({
    filterParamKeys: FILTER_PARAM_KEYS,
    readFiltersFromUrl,
    onUrlChange: useCallback((data) => {
      setAcademicYear(data.academicYear);
      setFacultyType(data.facultyType);
      setCreatedDateFrom(data.createdDateFrom);
      setCreatedDateTo(data.createdDateTo);
      setActiveStatusTab(data.activeStatusTab);
    }, []),
  });

  const currentPageQueryString = searchParams.get("current-page");
  const pageSizeQueryString = searchParams.get("page-size");

  const handleAcademicYearChange = useCallback(
    (selected) => {
      setAcademicYear(selected);
      setCreatedDateFrom("");
      setCreatedDateTo("");
      updateFilterParams({
        [FILTER_PARAM_KEYS.ACADEMIC_YEAR]: selected?.value || null,
        [FILTER_PARAM_KEYS.DATE_FROM]: null,
        [FILTER_PARAM_KEYS.DATE_TO]: null,
      });
    },
    [updateFilterParams]
  );

  const handleCreatedDateFromChange = useCallback(
    (value) => {
      setCreatedDateFrom(value);
      setAcademicYear(null);
      updateFilterParams({
        [FILTER_PARAM_KEYS.DATE_FROM]: value || null,
        [FILTER_PARAM_KEYS.ACADEMIC_YEAR]: null,
      });
    },
    [updateFilterParams]
  );

  const handleCreatedDateToChange = useCallback(
    (value) => {
      setCreatedDateTo(value);
      setAcademicYear(null);
      updateFilterParams({
        [FILTER_PARAM_KEYS.DATE_TO]: value || null,
        [FILTER_PARAM_KEYS.ACADEMIC_YEAR]: null,
      });
    },
    [updateFilterParams]
  );

  const handleFacultyTypeChange = useCallback(
    (selected) => {
      setFacultyType(selected);
      updateFilterParams({
        [FILTER_PARAM_KEYS.FACULTY_TYPE]: selected?.value || null,
      });
    },
    [updateFilterParams]
  );

  const handleStatusTabChange = useCallback(
    (updaterOrValue) => {
      setActiveStatusTab((prev) => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue;
        updateFilterParams({
          [FILTER_PARAM_KEYS.STATUS]: next || null,
        });
        return next;
      });
    },
    [updateFilterParams]
  );

  const handleClearAllFilters = useCallback(() => {
    setAcademicYear(null);
    setFacultyType(null);
    setCreatedDateFrom("");
    setCreatedDateTo("");
    setActiveStatusTab(null);
    setSearch("");
    clearFilterParams();
  }, [clearFilterParams, setSearch]);

  const dateRangeFromAcademicYear = React.useMemo(
    () =>
      academicYear?.value ? getAcademicYearDateRange(academicYear.value) : null,
    [academicYear]
  );
  const dateFrom =
    (dateRangeFromAcademicYear?.from ?? createdDateFrom) || undefined;
  const dateTo = (dateRangeFromAcademicYear?.to ?? createdDateTo) || undefined;
  const facultyTypes = React.useMemo(
    () => (facultyType?.value ? [facultyType.value] : undefined),
    [facultyType?.value]
  );
  const studentStatuses = React.useMemo(
    () => (activeStatusTab ? [activeStatusTab] : undefined),
    [activeStatusTab]
  );

  const fetchUsers = useCallback(
    async () =>
      await studentService.getStudents({
        currentPage: currentPageQueryString,
        pageSize: pageSizeQueryString,
        dateFrom,
        dateTo,
        facultyTypes,
        studentStatuses,
        search: searchQuery,
      }),
    [
      currentPageQueryString,
      pageSizeQueryString,
      dateFrom,
      dateTo,
      facultyTypes,
      studentStatuses,
      searchQuery,
    ]
  );

  const handleOnLoadFetchedUsers = useCallback(
    (res) => {
      setStudents(res?.data);
      setCurrentPage(res?.currentPage);
      setTotalPages(res?.totalPages);
      setTotalRecords(res?.totalRecords);
      setPageSize(res?.pageSize);
    },
    [setStudents, setCurrentPage, setTotalPages, setTotalRecords, setPageSize]
  );

  const studentDataChecker = useCallback(
    () => (students?.length > 0 ? true : false),
    [students]
  );

  const onExportClick = async () => {
    const copiedStudents = [...students];
    const checkedStudents = copiedStudents.filter((s) => s?.check === true);
    const studentsToExport = checkedStudents.map((student, index) => {
      const urls = student?.urls ?? [];
      const extraDocs = urls.length ? urls.join(", ") : "-";

      return {
        N: index + 1,
        "Application date": formatDate(student?.createdAt, "DD/MM/YYYY"),
        Name: student?.firstName,
        "Last Name": student?.lastName,
        "Middle Name": student?.middleName,
        "Date of Birth": formatDate(student?.dateOfBirth, "DD/MM/YYYY"),
        "Email address": student?.email,
        "Phone number": student?.phoneNumber,
        "Passport series and number": student?.passportSeriesAndNumber,
        "Fee type": student?.feeType,
        "Faculty type": student?.facultyType,
        "Photo url": student?.photoUrl ?? "-",
        "Passport url": student?.passportUrl ?? "-",
        IELTS: student?.englishDegree?.hasEnglishLanguageProficiencyEvidence
          ? "Yes"
          : "No",
        "IELTS link":
          student?.englishDegree?.englishProficiencyEvidenceCertificateUrl ?? "-",
        GCSE: urls[0] ?? "-",
        Transcript: urls[1] ?? "-",
        "GCSE link": urls[0] ?? "-",
        ID: student?.id,
        Reply: student?.reply ?? "-",
        Contract: student?.contractUrl ?? "-",
        Disability: student?.hasDisability ? "Yes" : "No",
        Country: student?.country ?? "-",
        City: student?.address?.city ?? "-",
        District: student?.address?.district ?? "-",
        Street: student?.address?.street ?? "-",
        "Home number": student?.instruction ?? "-",
        "Postal code": student?.postalCode ?? "-",
        "Know us":
          student?.hearAboutUs === "other"
            ? student?.hearAboutUsOther ||
              HEAR_ABOUT_US_READABLE[student?.hearAboutUs]
            : HEAR_ABOUT_US_READABLE[student?.hearAboutUs] ??
              student?.hearAboutUs ??
              "-",
        "Extra documents": extraDocs,
        Comment: student?.comment ?? "-",
        "Where Study": student?.whereStudy ?? "-",
      };
    });

    const newWorkBook = XLSX.utils.book_new();
    const newWorkSheet = XLSX.utils.json_to_sheet(studentsToExport);
    const body = checkedStudents.map((student) => student?.id);

    XLSX.utils.book_append_sheet(newWorkBook, newWorkSheet, "Sheet1");

    return await onFetch({
      action: () => studentService.updateStudentStatus("VIEWED", body),
      onLoad: () => {
        XLSX.writeFile(newWorkBook, "admission-students.xlsx");
        setStudents((prev) =>
          prev.map((student) => ({ ...student, check: false }))
        );
      },
      successMessage: "Excel file successfully created",
    });
  };

  return (
    <MainPageHOC
      headerTitle={"Students"}
      pageHeader={
        <MainPageHeader
          totalRecords={totalRecords}
          pageName="Students"
          redirectTo={"add"}
          disableExportButton={students?.find(
            (student) => student?.check === true
          )}
          showExportButton={true}
          onExportClick={onExportClick}
        />
      }
      filtersSection={
        <StudentsPageFilters
          academicYear={academicYear}
          setAcademicYear={handleAcademicYearChange}
          facultyType={facultyType}
          setFacultyType={handleFacultyTypeChange}
          createdDateFrom={createdDateFrom}
          setCreatedDateFrom={handleCreatedDateFromChange}
          createdDateTo={createdDateTo}
          setCreatedDateTo={handleCreatedDateToChange}
          activeStatusTab={activeStatusTab}
          setActiveStatusTab={handleStatusTabChange}
          onClearAllFilters={handleClearAllFilters}
        />
      }
      tableHeader={
        <MainPageTableHeader
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchValue={search}
          onSearchChange={setSearch}
        />
      }
      tableBody={
        <FetcherComponent
          key={[
            currentPageQueryString,
            pageSizeQueryString,
            dateFrom,
            dateTo,
            facultyTypes?.join(","),
            searchQuery,
          ]
            .filter(Boolean)
            .join("-")}
          action={fetchUsers}
          onLoad={handleOnLoadFetchedUsers}
        >
          <>
            {students?.length > 0 ? (
              <StudentsPageTableBody users={students} setUsers={setStudents} />
            ) : (
              <div className="d-flex justify-content-center align-items-center pt-4">
                <h4 className="title">No data found</h4>
              </div>
            )}
            <div className="card-inner">
              <PaginationComponent
                currentPageNumber={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                status={pageSizeQueryString}
              />
            </div>
          </>
        </FetcherComponent>
      }
      hasDataForRendering={studentDataChecker}
    />
  );
};
export default StudentsPage;
